import React, {createContext, useState, useEffect, ReactNode} from 'react'
import axios from 'axios'
import {IntlProvider} from 'react-intl'

import loadScript from '../../../utils/network/loadScript'
import retryAxios from '../../../utils/network/retryAxios'
import {logAndCaptureErrorMessage} from '../../../utils/logging/errorHandlers'

import {
  getMonthNames,
  getFirstDayOfWeek,
  getWeekDayNamesShort
} from '../../services/dateLocalization'

import {
  localeLanguageMapper,
  reactIntlLocaleMapper,
  mapArabicToLatinNumberSystem
} from '../../services/mappingLocales'

require(`@formatjs/intl-pluralrules/polyfill`)
require(`@formatjs/intl-relativetimeformat/polyfill`)

/**
 * Loads required assets and configurations for the provided locale.
 * Wraps its children in a `react-intl` provider and a custom provider that
 * makes the i18n assets and configurations available.
 */
export interface ConfigureI18nProps {
  /** The base URL where the application's static files (including the i18n assets) are hosted */
  publicUrl?: string
  /** The i18n assets will be configured for this locale */
  locale: string
  /** The user country code */
  countryCode: string
  /** The tree of components that will have access to the i18n context */
  children: ReactNode
}

const defaultI18nContextValues = {
  messages: [],
  monthNames: [],
  firstDayOfWeek: 1,
  weekDayNamesShort: []
}
/**
 * Custom context that makes the i18n assets and configurations available.
 */
export const ConfigureI18nContext = createContext(defaultI18nContextValues)

const ConfigureI18n = ({
  publicUrl = '/',
  locale,
  countryCode,
  children
}: ConfigureI18nProps) => {
  const [assets, setLoadedAssets] = useState(null)
  const [nonCriticalAssets, setNonCriticalAssetsLoaded] = useState(null)

  const languageCode = localeLanguageMapper(locale)
  const reactIntlLocaleCode = reactIntlLocaleMapper(languageCode)
  const mappedNumberSystemLocale = mapArabicToLatinNumberSystem(languageCode)

  const libPluralFolder = `${publicUrl}js/intl-pluralrules/locale-data/`
  const libRelativeFolder = `${publicUrl}js/intl-relativetimeformat/locale-data/`
  const translationsFolder = `${publicUrl}translations/`

  useEffect(() => {
    const pluralRulesLocale = loadScript(
      `${libPluralFolder}${reactIntlLocaleCode}.js`
    )

    const relativeTimeFormatLocale = loadScript(
      `${libRelativeFolder}${reactIntlLocaleCode}.js`
    )

    const axiosInstance = axios.create()
    retryAxios({axiosInstance, retryAttempts: 2, delayTime: 200})
    const loadTranslations = axiosInstance
      .get(`${translationsFolder}${languageCode}.json`)
      .then(res => res.data)

    const monthNames = getMonthNames(languageCode, publicUrl)
    const firstDayOfWeek = getFirstDayOfWeek(countryCode)
    const weekDayNamesShort = getWeekDayNamesShort(languageCode, publicUrl)

    const nonCriticalAssets = [monthNames, firstDayOfWeek, weekDayNamesShort]

    Promise.all(nonCriticalAssets).then(response => {
      const [monthNames, firstDayOfWeek, weekDayNamesShort] = response
      setNonCriticalAssetsLoaded({
        monthNames,
        firstDayOfWeek,
        weekDayNamesShort
      })
    })

    Promise.all([loadTranslations, pluralRulesLocale, relativeTimeFormatLocale])
      .then(response => {
        setLoadedAssets(response)
      })
      .catch((error: Error) => {
        // This error may come from Axios or loadScript
        logAndCaptureErrorMessage(
          `An error occurred while loading i18n assets - ${error.message}`,
          {level: 'error', tags: {locale}}
        )
        setLoadedAssets([{}, [], 1, []])
      })
  }, [locale])

  if (assets) {
    const [messages] = assets
    const contextAssets = {
      messages,
      ...nonCriticalAssets
    }

    return (
      <IntlProvider
        textComponent="span"
        locale={mappedNumberSystemLocale}
        messages={messages}
      >
        <ConfigureI18nContext.Provider value={contextAssets}>
          {children}
        </ConfigureI18nContext.Provider>
      </IntlProvider>
    )
  }

  return null
}

export default ConfigureI18n
